import { readonly, ref, type Ref } from 'vue'

/**
 * Toasts: the one place a completed write says so.
 *
 * Module-level state, because a toast raised by a store action must reach the region
 * mounted in `App.vue` with no component in between. Four tones, each carrying three
 * signals in the region (glyph, word, colour).
 *
 * A toast is for **outcomes** — "Role saved", "Sessions revoked" — never for validation,
 * which belongs beside the field, and never for a request failure a form can show in
 * place. A failure with nowhere else to go (a background invalidation, a delete from a
 * table row) is a `danger` toast that stays until dismissed.
 */
export interface Toast {
  readonly id: number
  readonly tone: 'ok' | 'info' | 'warn' | 'danger'
  readonly title: string
  readonly body?: string
  /** `danger` toasts do not auto-dismiss: an error that vanishes is an error nobody read. */
  readonly sticky: boolean
}

const toasts = ref<readonly Toast[]>([])
let nextId = 1

const AUTO_DISMISS_MS = 6_000

function dismiss(id: number): void {
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

function push(tone: Toast['tone'], title: string, body?: string): number {
  const id = nextId++
  const sticky = tone === 'danger'

  toasts.value = [...toasts.value, { id, tone, title, ...(body !== undefined ? { body } : {}), sticky }]

  if (!sticky) {
    setTimeout(() => {
      dismiss(id)
    }, AUTO_DISMISS_MS)
  }

  return id
}

export function useToast(): {
  toasts: Readonly<Ref<readonly Toast[]>>
  success: (title: string, body?: string) => number
  info: (title: string, body?: string) => number
  warn: (title: string, body?: string) => number
  error: (title: string, body?: string) => number
  dismiss: (id: number) => void
  /** Tests only. */
  clear: () => void
} {
  return {
    toasts: readonly(toasts),
    success: (title, body) => push('ok', title, body),
    info: (title, body) => push('info', title, body),
    warn: (title, body) => push('warn', title, body),
    error: (title, body) => push('danger', title, body),
    dismiss,
    clear: () => {
      toasts.value = []
    },
  }
}
